<template>
  <v-container fluid class="dashboard-container pa-0 pb-12">
    <!-- Header Principal Corporativo -->
    <v-sheet color="white" class="pa-6 border-bottom">
      <v-row align="center" justify="space-between" no-gutters>
        <v-col cols="12" md="6" class="d-flex align-center">
          <v-avatar size="48" color="primary lighten-5" class="mr-3 rounded-lg">
            <v-icon color="primary">fas fa-industry</v-icon>
          </v-avatar>
          <div>
            <h1 class="text-h5 font-weight-black grey--text text--darken-4 tracking-tight mb-0">
              Dashboard de Producción
            </h1>
            <div class="caption font-weight-bold grey--text">
              Rendimiento y avance en piso · últimos {{ days }} días
              <span v-if="asOfLabel">· al {{ asOfLabel }}</span>
              <span class="primary--text">· {{ selectedLineName }}</span>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="6" class="d-flex align-center justify-md-end mt-4 mt-md-0 flex-wrap" style="gap: 10px">
          <v-btn
            outlined
            color="grey darken-1"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            @click="$router.push({ name: 'ProductionBitacora' })"
          >
            <v-icon left small>fas fa-list-alt</v-icon>
            Bitácora
          </v-btn>

          <v-btn
            outlined
            color="primary"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            :loading="downloadingPdf"
            @click="downloadExecutivePdf"
          >
            <v-icon left small color="error">fas fa-file-pdf</v-icon>
            Exportar PDF
          </v-btn>

          <v-btn
            color="primary"
            depressed
            class="rounded-xl px-5 font-weight-bold text-none shadow-xs"
            :loading="loading"
            @click="load(true)"
          >
            <v-icon left small>fas fa-sync-alt</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="px-6 pt-6">
      <!-- Selector de Período Rápido -->
      <v-card flat class="panel-card mb-6 shadow-xs">
        <v-row align="center" justify="space-between" dense>
          <v-col cols="12" md="8" class="d-flex align-center flex-wrap" style="gap: 8px">
            <span class="caption font-weight-black text-uppercase grey--text mr-2">Período de Análisis:</span>
            <div class="d-flex flex-wrap" style="gap: 6px">
              <v-btn
                v-for="preset in dayPresets"
                :key="preset.value"
                x-small
                depressed
                class="rounded-pill font-weight-bold text-none"
                :color="days === preset.value ? 'primary' : 'grey lighten-4'"
                :class="days === preset.value ? 'white--text' : 'grey--text text--darken-2'"
                @click="days = preset.value"
              >
                {{ preset.label }}
              </v-btn>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="line"
              :items="businessLineOptions"
              item-value="id"
              item-text="name"
              label="Línea de negocio"
              prepend-inner-icon="fas fa-layer-group"
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>
      </v-card>

      <!-- Estado de Error con Reintento -->
      <v-alert
        v-if="error"
        dense
        outlined
        type="error"
        class="mb-6 rounded-xl bg-white"
      >
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <span>{{ error }}</span>
          <v-btn small color="error" depressed class="rounded-lg text-none font-weight-bold" @click="load(true)">
            <v-icon left small>fas fa-redo</v-icon>
            Reintentar
          </v-btn>
        </div>
      </v-alert>

      <!-- Skeletons de Carga -->
      <v-row v-if="loading && !asOf" dense class="mb-6">
        <v-col v-for="i in 4" :key="'sk-' + i" cols="12" sm="6" md="3">
          <v-skeleton-loader type="card" height="136" class="rounded-xl shadow-xs" />
        </v-col>
      </v-row>

      <template v-else>
        <!-- Fila Héroe de 4 KPIs Maestros -->
        <v-row dense class="mb-6">
          <!-- KPI 1: Producción de Hoy (North Star) -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100 is-primary">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Producción de Hoy</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Metros cuadrados procesados y reportados durante el turno actual de hoy.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value primary--text text--darken-3">
                  {{ kpis.today_m2 | number('0,0.0') }} <span class="unit-text">m²</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      {{ kpis.today_qty | number('0,0.0') }} un · {{ kpis.today_events || 0 }} rep.
                    </span>
                    <span class="caption font-weight-black" :class="deltaClass(kpis.vs_yesterday_pct)">
                      {{ deltaLabel(kpis.vs_yesterday_pct) }} vs ayer
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 2: Total Acumulado Período -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Total Período ({{ days }} Días)</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Suma acumulada de metros cuadrados procesados en los últimos {{ days }} días.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value grey--text text--darken-4">
                  {{ kpis.m2 | number('0,0.0') }} <span class="unit-text">m²</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      {{ kpis.qty | number('0,0.0') }} un · {{ kpis.events || 0 }} rep.
                    </span>
                    <span class="caption font-weight-black" :class="deltaClass(kpis.vs_previous_pct)">
                      {{ deltaLabel(kpis.vs_previous_pct) }} vs prev.
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 3: Producción Ayer -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Turno de Ayer</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Total de metros cuadrados producidos en el día calendario inmediatamente anterior.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value teal--text text--darken-3">
                  {{ kpis.yesterday_m2 | number('0,0.0') }} <span class="unit-text">m²</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="caption font-weight-bold grey--text text--darken-1">
                    {{ kpis.yesterday_qty | number('0,0.0') }} un · {{ kpis.yesterday_events || 0 }} reportes
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 4: OTs Trabajadas -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">OTs Activas en Taller</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Órdenes de Trabajo distintas que registraron movimientos reales en el período.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value grey--text text--darken-4">
                  {{ kpis.ots || 0 }} <span class="unit-text">OT</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="caption font-weight-bold grey--text text--darken-1">
                    Con avance real reportado en taller
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Producción por Línea de Negocio -->
        <v-card class="panel-card mb-6 shadow-xs" flat>
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="panel-title">Por Línea de Negocio</div>
            <span class="caption grey--text font-weight-bold">Cada línea mide su avance en m² y unidades</span>
          </div>
          <div class="panel-sub mb-4">Todo el período, sin aplicar el filtro de línea · clic en una fila para filtrar el dashboard</div>

          <div v-if="!lineBreakdown.length" class="empty-mini py-6 text-center">Sin producción en este período</div>
          <v-simple-table v-else class="zero-table">
            <thead>
              <tr>
                <th>Línea</th>
                <th class="text-end">m²</th>
                <th class="text-end">Unidades</th>
                <th class="text-end">Reportes</th>
                <th class="text-end">OTs</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in lineBreakdown"
                :key="'l-' + row.id"
                class="line-row"
                :class="{ 'is-selected-line': String(line) === String(row.id) }"
                @click="toggleLine(row.id)"
              >
                <td class="font-weight-bold">{{ row.name }}</td>
                <td class="text-end font-weight-black">{{ row.m2 | number('0,0.0') }}</td>
                <td class="text-end font-weight-black">{{ row.qty | number('0,0.0') }}</td>
                <td class="text-end">{{ row.events }}</td>
                <td class="text-end">{{ row.ots }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>

        <!-- Comparativa Diaria: Strip + Tabla Compacta -->
        <v-card class="panel-card mb-6 shadow-xs" flat>
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="panel-title">Evolución y Comparativa Diaria</div>
            <span class="caption grey--text font-weight-bold">Avance en m² vs día anterior</span>
          </div>
          <div class="panel-sub mb-4">Registro continuo de rendimiento diario en el período auditado</div>

          <div v-if="!(daily && daily.length)" class="empty-mini py-10 text-center">
            <v-icon color="grey lighten-1" class="mb-2" large>fas fa-chart-bar</v-icon>
            <div>Sin producción registrada en este período</div>
          </div>

          <template v-else>
            <!-- Ticker de Barras Diarias -->
            <div class="day-strip mb-6 pa-3 rounded-lg">
              <div
                v-for="day in daily"
                :key="day.date"
                class="day-cell"
                :class="{ 'is-empty': !day.events, 'is-today': day.is_today }"
              >
                <span class="day-name">{{ day.label }}</span>
                <span class="day-bar-track">
                  <span class="day-bar" :style="{ height: barHeight(day.m2) }"></span>
                </span>
                <span class="day-count">{{ compact(day.m2) }}</span>
                <span class="delta xs" :class="deltaClass(day.vs_prev_pct)">{{ deltaShort(day.vs_prev_pct) }}</span>
              </div>
            </div>

            <!-- Tabla Detallada Últimos 14 Días -->
            <v-simple-table class="zero-table">
              <thead>
                <tr>
                  <th>Día</th>
                  <th class="text-end">m²</th>
                  <th class="text-end">vs día anterior</th>
                  <th class="text-end">Unidades</th>
                  <th class="text-end">Reportes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="day in dailyTable" :key="'t-' + day.date" :class="{ 'is-today-row': day.is_today }">
                  <td>
                    <span class="font-weight-bold">{{ day.label }}</span>
                    <span v-if="day.is_today" class="caption teal--text font-weight-bold ml-1">(Hoy)</span>
                  </td>
                  <td class="text-end font-weight-black">{{ day.m2 | number('0,0.0') }}</td>
                  <td class="text-end">
                    <span class="delta font-weight-bold" :class="deltaClass(day.vs_prev_pct)">{{ dayDelta(day) }}</span>
                  </td>
                  <td class="text-end">{{ day.qty | number('0,0.0') }}</td>
                  <td class="text-end">{{ day.events }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>
        </v-card>

        <!-- Rankings Operativos en 3 Columnas (Por Área, Por Máquina, Por Operador) -->
        <v-row dense class="mb-6">
          <!-- Columna 1: Por Área -->
          <v-col cols="12" md="4">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Por Área</div>
                <span class="caption grey--text font-weight-bold">{{ areas.length }} áreas</span>
              </div>
              <div class="panel-sub mb-4">Proceso y centro de trabajo</div>

              <div v-if="!areas.length" class="empty-mini py-6 text-center">Sin producción por área</div>
              <div v-for="row in areas" :key="'a-' + row.id" class="rank-row">
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">{{ row.name }}</div>
                  <div class="rank-val font-weight-bold">{{ row.m2 | number('0,0.0') }} m²</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.events }} reportes · {{ row.qty | number('0,0.0') }} un</div>
                </div>
                <div class="rank-track">
                  <div class="rank-fill" :style="{ width: rankWidth(row.m2, maxArea) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Columna 2: Por Máquina -->
          <v-col cols="12" md="4">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Por Máquina</div>
                <span class="caption grey--text font-weight-bold">{{ machines.length }} equipos</span>
              </div>
              <div class="panel-sub mb-4">Equipo que reportó en piso</div>

              <div v-if="!machines.length" class="empty-mini py-6 text-center">Sin producción por máquina</div>
              <div v-for="row in machines" :key="'m-' + row.id" class="rank-row">
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">{{ row.name }}</div>
                  <div class="rank-val font-weight-bold">{{ row.m2 | number('0,0.0') }} m²</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.events }} reportes · {{ row.qty | number('0,0.0') }} un</div>
                </div>
                <div class="rank-track">
                  <div class="rank-fill is-teal" :style="{ width: rankWidth(row.m2, maxMachine) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Columna 3: Por Operador -->
          <v-col cols="12" md="4">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Por Operador</div>
                <span class="caption grey--text font-weight-bold">{{ operators.length }} operadores</span>
              </div>
              <div class="panel-sub mb-4">Quién reportó el avance en piso</div>

              <div v-if="!operators.length" class="empty-mini py-6 text-center">Sin reportes de operadores</div>
              <div v-for="row in operators" :key="'o-' + row.id" class="rank-row">
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">{{ row.name }}</div>
                  <div class="rank-val font-weight-bold">{{ row.m2 | number('0,0.0') }} m²</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.events }} reportes · {{ row.qty | number('0,0.0') }} un</div>
                </div>
                <div class="rank-track">
                  <div class="rank-fill is-orange" :style="{ width: rankWidth(row.m2, maxOperator) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Panel de Últimos Movimientos Reportados -->
        <v-card class="panel-card shadow-xs" flat>
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="panel-title">Últimos Reportes de Avance</div>
            <span class="caption grey--text font-weight-bold">Trazabilidad en tiempo real</span>
          </div>
          <div class="panel-sub mb-4">Movimientos físicos más recientes registrados en la bitácora de piso</div>

          <div v-if="!recent.length" class="empty-mini py-6 text-center">Aún no hay movimientos en este período</div>
          <v-simple-table v-else class="zero-table">
            <thead>
              <tr>
                <th>Hora</th>
                <th>OT</th>
                <th>Operador</th>
                <th>Área / Máquina</th>
                <th class="text-end">Avance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in recent" :key="row.id">
                <td class="caption font-weight-bold grey--text">{{ timeLabel(row.at) }}</td>
                <td>
                  <show-work-order v-if="row.ot" :work_order_id="row.ot" class="ot-id">
                    OT {{ row.ot }}
                  </show-work-order>
                  <div class="caption grey--text text-truncate" style="max-width: 250px">
                    {{ row.process || row.product }}
                  </div>
                  <div v-if="row.line" class="caption primary--text font-weight-bold">{{ row.line }}</div>
                </td>
                <td class="font-weight-medium">{{ row.operator || '—' }}</td>
                <td>
                  <div class="font-weight-medium">{{ row.area || '—' }}</div>
                  <div class="caption grey--text">{{ row.equipment }}</div>
                </td>
                <td class="text-end">
                  <div class="font-weight-black text-subtitle-2 mb-0">{{ row.m2 | number('0,0.00') }} m²</div>
                  <div class="caption grey--text">{{ row.qty | number('0,0.00') }} un</div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </template>
    </div>
  </v-container>
</template>

<script>
import { fetchProductionDashboard } from '@/api/reports'
import { generateProductionDashboardPdf } from '@/doc_generators/productionDashboardReport'
import notifications from '@/utils/notifications'
import moment from 'moment'
import businessLineFilter from '@/mixins/businessLineFilter'

export default {
  name: 'ProductionDashboard',
  mixins: [businessLineFilter],
  data() {
    return {
      loading: true,
      ready: false,
      requesting: false,
      downloadingPdf: false,
      error: '',
      days: 7,
      dayPresets: [
        { label: '7 días', value: 7 },
        { label: '30 días', value: 30 },
        { label: '90 días', value: 90 }
      ],
      lastQuery: '',
      asOf: '',
      kpis: {
        events: 0,
        ots: 0,
        m2: 0,
        qty: 0,
        today_m2: 0,
        today_qty: 0,
        today_events: 0,
        yesterday_m2: 0,
        yesterday_qty: 0,
        yesterday_events: 0,
        vs_yesterday_pct: null,
        vs_previous_pct: null
      },
      areas: [],
      machines: [],
      operators: [],
      daily: [],
      recent: []
    }
  },
  computed: {
    asOfLabel() {
      return this.asOf ? moment(this.asOf).format('DD/MM/YYYY') : '—'
    },
    maxArea() {
      return this.maxOf(this.areas)
    },
    maxMachine() {
      return this.maxOf(this.machines)
    },
    maxOperator() {
      return this.maxOf(this.operators)
    },
    maxDay() {
      return this.maxOf(this.daily)
    },
    dailyTable() {
      return this.daily.slice().reverse().slice(0, 14)
    }
  },
  methods: {
    maxOf(rows) {
      let max = 1
      ;(rows || []).forEach((row) => {
        const value = Number(row.m2 || 0)
        if (value > max) max = value
      })
      return max
    },
    rankWidth(value, max) {
      return Math.max(8, Math.round((Number(value || 0) / Math.max(1, max)) * 100)) + '%'
    },
    barHeight(value) {
      if (!value) return '4px'
      return Math.max(6, Math.round((Number(value) / this.maxDay) * 48)) + 'px'
    },
    compact(value) {
      const n = Number(value || 0)
      if (!n) return '—'
      if (n >= 1000) return Math.round(n / 100) / 10 + 'k'
      return Math.round(n)
    },
    deltaClass(value) {
      if (value == null) return ''
      if (value > 0) return 'is-up'
      if (value < 0) return 'is-down'
      return ''
    },
    deltaLabel(value) {
      if (value == null) return 'sin base'
      const sign = value > 0 ? '+' : ''
      return sign + value + '%'
    },
    deltaShort(value) {
      if (value == null) return ''
      if (value > 0) return '+' + Math.round(value)
      return String(Math.round(value))
    },
    dayDelta(day) {
      if (day.vs_prev == null) return '—'
      const sign = day.vs_prev > 0 ? '+' : ''
      return sign + this.$options.filters.number(day.vs_prev, '0,0.0') + '  ' + this.deltaLabel(day.vs_prev_pct)
    },
    timeLabel(value) {
      return value ? moment(value).format('DD/MM HH:mm') : '—'
    },
    async load(force) {
      const query = 'days=' + (Number(this.days) || 7) + this.lineQuery()
      if (this.requesting) return
      if (!force && this.lastQuery === query && this.asOf) return
      this.requesting = true
      this.loading = true
      this.error = ''
      try {
        const data = await fetchProductionDashboard(query)
        if (!data || !data.as_of) throw new Error('Respuesta vacía')
        this.asOf = data.as_of
        this.lastQuery = query
        this.kpis = Object.assign({}, this.kpis, data.kpis || {})
        this.setLineBreakdown(data.lines)
        this.areas = data.areas || []
        this.machines = data.machines || []
        this.operators = data.operators || []
        this.daily = data.daily || []
        this.recent = data.recent || []
        this.ready = true
      } catch (err) {
        this.error = 'No se pudo cargar la producción del taller.'
        notifications.showError('Error cargando producción: ' + err)
      } finally {
        this.requesting = false
        this.loading = false
      }
    },
    async downloadExecutivePdf() {
      if (this.downloadingPdf) return
      this.downloadingPdf = true
      try {
        const company = this.$store.getters.getCompany || {}
        await generateProductionDashboardPdf({
          company,
          days: this.days,
          asOf: this.asOf,
          lineName: this.selectedLineName,
          lines: this.lineBreakdown,
          serverTimezone: company.current_time_zone || 'America/Santiago',
          kpis: this.kpis,
          areas: this.areas,
          machines: this.machines,
          operators: this.operators,
          daily: this.daily,
          recent: this.recent
        })
        notifications.showSuccess('Reporte PDF de producción generado correctamente.')
      } catch (err) {
        notifications.showError('No se pudo generar el reporte PDF: ' + (err.message || err))
      } finally {
        this.downloadingPdf = false
      }
    }
  },
  watch: {
    days(val, oldVal) {
      if (!this.ready || Number(val) === Number(oldVal)) return
      this.load()
    },
    line(val, oldVal) {
      if (!this.ready || String(val) === String(oldVal)) return
      this.load()
    }
  },
  mounted() {
    this.load(true)
  }
}
</script>

<style scoped>
.dashboard-container {
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.border-bottom {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08) !important;
}

.shadow-xs {
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02) !important;
}

.tracking-tight {
  letter-spacing: -0.02em;
}

/* Tarjetas Héroe de KPIs (Idénticas a CommercialDashboard) */
.hero-kpi-card {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02);
}

.hero-kpi-card:hover {
  border-color: rgba(15, 23, 42, 0.15);
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.06), 0 2px 4px -1px rgba(15, 23, 42, 0.03);
}

.hero-kpi-card.is-primary {
  border-color: #bbf7d0;
  background: linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%);
}

.kpi-label {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.kpi-value {
  font-size: 1.65rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
}

.unit-text {
  font-size: 0.95rem;
  font-weight: 700;
  color: #64748b;
}

.kpi-footer {
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.delta {
  font-size: 0.75rem;
  font-weight: 800;
}

.delta.is-up {
  color: #10b981;
}

.delta.is-down {
  color: #ef4444;
}

.delta.xs {
  font-size: 9px;
}

/* Paneles de Contenido */
.panel-card {
  background: #ffffff !important;
  border: 1px solid rgba(15, 23, 42, 0.08) !important;
  border-radius: 16px !important;
  padding: 20px 22px;
}

.panel-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
}

.panel-sub {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

/* Rankings */
.rank-row {
  margin-bottom: 12px;
}

.rank-row:last-child {
  margin-bottom: 0;
}

.rank-name {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.rank-meta {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.rank-track {
  height: 6px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 4px;
}

.rank-fill {
  height: 100%;
  background: #00a650;
  border-radius: 4px;
}

.rank-fill.is-teal {
  background: #0284c7;
}

.rank-fill.is-orange {
  background: #f59e0b;
}

.rank-val {
  font-size: 12.5px;
  font-weight: 800;
  color: #0f172a;
}

/* Ticker Diario */
.day-strip {
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.06);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(36px, 1fr));
  gap: 4px;
  overflow-x: auto;
}

.day-cell {
  min-width: 36px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.day-cell.is-today .day-name {
  color: #00a650;
  font-weight: 900;
}

.day-name {
  font-size: 9px;
  font-weight: 800;
  color: #94a3b8;
}

.day-bar-track {
  height: 52px;
  display: flex;
  align-items: flex-end;
  margin: 4px 0 2px;
}

.day-bar {
  width: 8px;
  border-radius: 3px;
  min-height: 4px;
  background: #00a650;
}

.day-cell.is-empty .day-bar {
  background: #e2e8f0;
}

.day-count {
  font-size: 10px;
  font-weight: 800;
  color: #0f172a;
}

.empty-mini {
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
}

.ot-id {
  font-size: 13px;
  font-weight: 800;
  color: #00a650;
}

.zero-table >>> th {
  font-size: 11px !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b !important;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08) !important;
}

.zero-table >>> td {
  border-bottom: 1px solid rgba(15, 23, 42, 0.04) !important;
}

.is-today-row {
  background: #f0fdf4;
}

.line-row {
  cursor: pointer;
}

.is-selected-line {
  background: #f0fdf4;
  box-shadow: inset 3px 0 0 #00a650;
}
</style>
